// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import { fontConfig } from "@vanmeter/XDL/constants";
import Previous from "@vanmeter/XDL/Icons/IconAngleLeftLarge";
import Next from "@vanmeter/XDL/Icons/IconAngleRightLarge";
import Calendar from "@vanmeter/XDL/Icons/IconCalendar";
import Close from "@vanmeter/XDL/Icons/IconClose";
import { placeholderStyle } from "@vanmeter/XDL/PreStyle/formField";
import { xBaseTheme } from "@vanmeter/XDL/types";
import color from "@vanmeter/XDL/Utilities/color";
import React from "react";
import { css } from "styled-components";

export default {
    defaultProps: {
        mergeCss: true,
        ...DatePickerPresentationPropsDefault,
        dateTimePickerProps: {
            calendarProps: {
                nextLabel: React.createElement(Next),
                prevLabel: React.createElement(Previous),
            } as any,
        },
        calendarIconProps: {
            ...DatePickerPresentationPropsDefault?.calendarIconProps,
            src: Calendar,
            size: 16,
            width: 14,
            color: "common.ui5",
            css: css`
                ${DatePickerPresentationPropsDefault?.calendarIconProps?.css}
                && {
                    padding: 9px 0;
                    margin-right: 0;
                }
                &:hover {
                    color: ${color("common.ui15")};
                }
            `,
        },
        clearIconProps: {
            ...DatePickerPresentationPropsDefault?.clearIconProps,
            src: Close,
            size: 12,
            height: 16,
            color: "common.ui5",
            css: css`
                ${DatePickerPresentationPropsDefault?.clearIconProps?.css}
                && {
                    padding: 0;
                    margin-right: 8px;
                }
                &:hover {
                    color: ${color("common.ui15")};
                }
            `,
        },
        placeholders: {
            dayPlaceholder: "DD",
            monthPlaceholder: "MM",
            yearPlaceholder: "YYYY",
        },
        cssOverrides: {
            datePicker: css`
                line-height: 16px;
                .react-calendar {
                    right: unset;
                    left: 0;
                    button {
                        font-family: ${fontConfig.fontFamily};
                    }

                    .react-calendar__navigation {
                        &__arrow {
                            height: auto;
                        }

                        &__prev-button,
                        &__next-button {
                            color: ${getColor("text")};
                            background-image: none;
                            background: transparent;
                            &:enabled {
                                &:focus:not(:disabled) {
                                    background: transparent;
                                    color: ${getColor("secondary")};
                                }

                                &:hover:not(:disabled) {
                                    background: transparent;
                                    color: ${getColor("secondary")};
                                }
                            }

                            &:disabled {
                                opacity: 0.33;
                                cursor: not-allowed;
                            }

                            svg {
                                width: 8px;
                                height: 14px;
                            }
                        }

                        &__label {
                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("text")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("secondary")};
                                }
                            }
                        }
                    }

                    button {
                        background-color: ${getColor("common.background")};
                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                color: ${getColor("common.white")};
                                background-color: ${getColor("secondary")};
                            }
                        }
                    }
                    &__tile {
                        &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
                        &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                            background-color: ${getColor("common.ui8")};

                            &:after {
                                border-color: ${getColor("common.ui1")};
                            }
                            &:hover {
                                color: ${({ theme }) => getContrastColor("secondary", theme)};
                            }
                        }
                    }
                    &__month-view {
                        &__weekdays {
                            font-size: ${fontConfig.xSmall.size}px;
                            font-weight: ${fontConfig.weight.bold};
                            color: ${getColor("common.ui6")};
                            text-transform: uppercase;
                            text-align: center;
                            abbr {
                                text-decoration: none;
                            }
                        }

                        &__days {
                            &__day {
                                border-radius: 0;
                                font-weight: normal;
                                font-size: ${fontConfig.xSmall.size}px;
                                height: 29px;
                                width: 29px;
                                padding: 0;

                                &.react-calendar__tile--now {
                                    font-weight: ${fontConfig.weight.bold};
                                    border: 1px solid ${color("common.ui8")};
                                    &:disabled {
                                        border-color: ${color("common.ui8", 0.33)};
                                    }

                                    &:hover {
                                        &:after {
                                            border-color: ${getColor("common.ui1")};
                                        }
                                    }
                                }

                                &:disabled,
                                &--neighboringMonth {
                                    color: ${getColor("common.border")};
                                }
                            }
                        }
                    }

                    &__year-view,
                    &__decade-view,
                    &__century-view {
                        .react-calendar__tile {
                            &:disabled,
                            &--now:disabled {
                                color: ${getColor("common.border")};
                                cursor: default;
                            }
                        }
                    }
                }
                .react-datetime-picker {
                    box-shadow: inset 0 1px 0 0 ${color("common.ui7", 0.1)};
                    color: ${color("common.ui7")};
                    min-height: 36px;
                    &__wrapper {
                        align-items: center;
                    }
                    &:not(:disabled) {
                        &:not(:focus-within):hover:not(:has(.react-datetime-picker__button:hover)) {
                            border-color: ${color("common.ui15")};
                        }
                        &:focus-within {
                            border-color: ${color("common.ui16")};
                        }
                    }
                    &:not(:disabled),
                    &:focus-within {
                        padding: 0 8px 0 8px;
                        .react-datetime-picker__inputGroup {
                            padding: 0 9px 0 0;
                        }
                        .react-datetime-picker__button {
                            padding-right: 0;
                            display: flex;
                            align-items: center;
                        }
                    }
                    &__inputGroup {
                        input::placeholder {
                            ${placeholderStyle}
                        }
                        &__input {
                            box-shadow: none;
                            ${placeholderStyle}
                            text-align: center;
                            &:placeholder-shown {
                                min-width: 28px;
                                padding: 0;
                            }

                            &:not(:placeholder-shown) + span {
                                color: ${color("text.main")};
                                font-style: normal;
                            }
                        }

                        &__month {
                            &:placeholder-shown {
                                min-width: 34px;
                            }
                        }

                        &__year {
                            &:placeholder-shown {
                                min-width: 45px;
                            }
                        }

                        &__divider {
                            color: ${color("common.ui4")};
                        }

                        &__leadingZero {
                            margin: 0;
                        }
                    }

                    &__button {
                        padding-right: 0;
                    }
                    &--disabled {
                        background: ${color("common.ui2")};
                        opacity: 0.33;
                        cursor: not-allowed;
                        button,
                        input,
                        span {
                            cursor: not-allowed !important;
                        }
                        .react-datetime-picker__button {
                            ${IconWrapper} {
                                color: ${color("common.ui3")};
                            }
                        }
                        &:hover {
                            border-color: ${color("common.ui5")} !important;
                        }
                    }
                }

                &[aria-invalid="true"] .react-datetime-picker {
                    border-color: ${color("danger")};
                }

                .react-datetime-picker__calendar--open,
                .react-datetime-picker__calendar--closed,
                .react-datetime-picker__clock--open,
                .react-datetime-picker__clock--closed {
                    position: absolute;
                }
                min-width: 162px;
            `,
            formField: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["datePicker"];
